<script setup>
import { showModal, hideModal } from '@/Utils/modalBus'
import { computed, ref, onMounted, getCurrentInstance } from 'vue'
import { useNotification } from "@kyvg/vue3-notification"
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import _ from 'lodash';
import { track, EVENTS } from '@/lib/tracking'
import Multiselect from 'vue-multiselect';
import StreamService from '@/services/StreamService';
import IntegrationService from '@/services/IntegrationService';
import ConfirmModal from '@/components/modals/ConfirmModal.vue';
import FormSwitch from '../../components/Atoms/FormSwitch.vue';
import FormButton from '../../components/Atoms/FormButton.vue';
import FormInput from '../../components/Atoms/FormInput.vue';
import FormText from '../../components/Atoms/FormText.vue';
import Spinner from '../../components/ui/Spinner.vue';
import FormCheckbox from '../../components/Atoms/FormCheckbox.vue'
import Tooltip from '../../components/Atoms/Tooltip.vue'
import IconBase from '../../components/icon/IconBase.vue';
import IconCopy from '../../components/icon/IconCopy.vue';

const { notify } = useNotification()
const store = useStore()
const router = useRouter()
const instance = getCurrentInstance();
const root = instance.proxy.$root;

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id');
		}
	}
})

const emit = defineEmits(['stream-updated'])

const streamId = ref(null)
const streamMeta = ref(null)
const countryBlockSelection = ref([])
const countryWhitelistSelection = ref([])
const countryList = ref([])
const countryListLoaded = ref(false)
const whitelistedDomains = ref([])
const whitelistedDomainsLoaded = ref(false)
const features = ref({
	drmProtection: {
		enabled: false,
		value: false,
		valueType: 'boolean'
	},
	embedPassword: {
		enabled: false,
		value: null,
		valueType: 'string'
	},
	countryBlock: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	countryWhitelists: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	domainWhitelists: {
		enabled: false,
		value: [],
		valueType: 'array'
	},
	pageWhitelist: {
		enabled: false,
		value: '',
		valueType: 'boolean'
	}
})
const featureProcessing = ref({
	drmProtection: false,
	embedPassword: false,
	countryBlock: false,
	countryWhitelists: false,
	domainWhitelists: false
})

const featureDisabled = ref({
	embedPassword: true,
	countryBlock: true,
	countryWhitelists: true,
	domainWhitelists: true
})

const productsFeatures = computed(() => {
	return store.state.User.aggregatedDefinition;
})

const securityWhitelistPage = computed(() => store.getters['User/securityWhitelistPage'])

const drmProtectionFlag = computed(() => store.getters['User/drmProtectionFlag'])

onMounted(async () => {
	{
		streamId.value = props.stream._id;

		const meta = await StreamService.getStreamMetadata(streamId.value);
		streamMeta.value = meta;

		_.forIn(meta, (value, key) => {
			if (value !== undefined && value !== null && key in features.value) {
				let enabled = value !== false;
				if (Array.isArray(value)) {
					enabled = value.length > 0;
				}

				features.value[key].enabled = enabled;
				if (value.constructor !== Boolean) {
					features.value[key].value = value;
				}
			}
		});

		// fetch country list
		countryList.value = await IntegrationService.getCountryList();
		countryListLoaded.value = true;

		countryBlockSelection.value = _.map(
			features.value.countryBlock.value,
			code => _.find(countryList.value, { code })
		);
		countryWhitelistSelection.value = _.map(
			features.value.countryWhitelists.value,
			code => _.find(countryList.value, { code })
		);

		whitelistedDomains.value = _.join(
			_.filter(features.value.domainWhitelists.value, item => item !== 'https://castr.com'),
			'\r\n',
		);
		if (features.value.domainWhitelists.value.length === 1 && features.value.domainWhitelists.value[0] === 'https://castr.com') {
			features.value.domainWhitelists.enabled = false;
		}
		whitelistedDomainsLoaded.value = true;
	}
})

const navigateToPayments = (category = 'vod') => {
	// Redirect to correct live or vod package
	if (category !== 'live' && category !== 'vod') {
		category = 'vod'
	}
	if (category === 'vod') {
		category = 'live'
	}
	router.push({ name: 'Payments', query: { category } });
}

const toggleFeature = async (featureName) => {
	if (featureProcessing.value[featureName]) return;

	const feature = features.value[featureName];
	if (!feature) return;

	let mappedSecurityFeatName = featureName;
	switch (featureName) {
		case 'embedPassword':
			mappedSecurityFeatName = 'securityFeaturesPasswordAuth';
			break;
		case 'countryBlock':
			mappedSecurityFeatName = 'securityFeaturesGeoBlock';
			break;
		case 'countryWhitelists':
			mappedSecurityFeatName = 'securityFeaturesGeoWhitelist';
			break;
		case 'domainWhitelists':
			mappedSecurityFeatName = 'securityFeaturesDomainWhitelist';
			break;
	}

	// Disable geo-blocking/whitelisting on those packages
	const data = _.filter(store.state.User.subscriptions, (item) => item.category === 'live' && item.enabled)[0];
	const disabledGeoFeaturePackages = [
		'5c6e7405b408c1b41dd32f46', // 1TB Bandwidth
		'5c6e7405b408c1b41dd32f47', // 1TB Bandwidth - Yearly
		'5c6e7406b408c1b41dd32f48', // 2TB Bandwidth
		'5c6e7406b408c1b41dd32f49', // 2TB Bandwidth - Yearly
		'5ccd92f8152b7f25111c39cf', // Live 5GB Bandwidth - 7 Day Trial
		'6362114709950a6a642540c3', // New All in One - 7 Days Trial
		'6010063d03afd67aa9831cd4', // All-In-One Entry Monthly
		'6361ecd609950a6a642540b7', // New All-In-One Entry Monthly
		'6018dbb703afd67aa9831ce3', // All-In-One Entry Yearly
		'6361ecd609950a6a642540b8', // New All-In-One Entry Yearly
		'636dcc7ced167e4759926fd9', // BF22 - All in One - Entry Yearly
		'6018dc7403afd67aa9831ce4', // All-In-One Plus Monthly
		'6361ecd609950a6a642540b9', // New All-In-One Plus Monthly
		'602cc7a503afd67aa9831ced', // All-In-One Plus Yearly
		'6361ecd609950a6a642540ba', // New All-In-One Plus Yearly
		'602cc91703afd67aa9831cf2', // All-In-One Premium Monthly
		'6361ecd609950a6a642540bf', // New All-In-One Premium Monthly
		'602cc95903afd67aa9831cf3', // All-In-One Premium Yearly
		'6361ecd609950a6a642540c0', // New All-In-One Premium Yearly
		'6434ffbd40771d569f1e98e6', // Starter 2023
		'6435032f40771d569f1e98f2', // Starter Yearly 2023
		'6435001640771d569f1e98e8', // Standard 2023
		'6435035f40771d569f1e98f4', // Standard Yearly 2023
		'6435007440771d569f1e98ea', // Professional 2023
		'643503a640771d569f1e98f6', // Professional Yearly 2023
	]

	if ((props.stream.type === 'live' && data && disabledGeoFeaturePackages.indexOf(data.package._id) !== -1
		&& (['countryBlock'].indexOf(featureName) !== -1)
		&& features.value.countryBlock.enabled) || (props.stream.type === 'live' && data && disabledGeoFeaturePackages.indexOf(data.package._id) !== -1
			&& (['countryWhitelists'].indexOf(featureName) !== -1)
			&& !features.value.countryBlock.enabled)
	) {
		showModal('feature-upgrade');
		setTimeout(() => {
			features.value[featureName].enabled = !features.value[featureName].enabled;
		}, 500);
		return;
	}

	const trialAndAIOEntryPackages = [
		'5ccd92f8152b7f25111c39cf', // Live 5GB Bandwidth - 7 Day Trial
		'6362114709950a6a642540c3', // New All in One - 7 Days Trial
		'6010063d03afd67aa9831cd4', // All-In-One Entry Monthly
		'6361ecd609950a6a642540b7', // New All-In-One Entry Monthly
		'6018dbb703afd67aa9831ce3', // All-In-One Entry Yearly
		'6361ecd609950a6a642540b8', // New All-In-One Entry Yearly
		'636dcc7ced167e4759926fd9', // BF22 - All in One - Entry Yearly
		'6434ffbd40771d569f1e98e6', // Starter 2023
		'6435032f40771d569f1e98f2', // Starter Yearly 2023
		'6435001640771d569f1e98e8', // Standard 2023
		'6435035f40771d569f1e98f4', // Standard Yearly 2023
	]
	if (props.stream.type === 'live' && data && trialAndAIOEntryPackages.indexOf(data.package._id) !== -1
		&& (['domainWhitelists'].indexOf(featureName) !== -1)
	) {
		showModal('feature-upgrade');
		setTimeout(() => {
			features.value[featureName].enabled = !features.value[featureName].enabled;
		}, 500);
		return;
	}

	if (!productsFeatures.value || productsFeatures.value[props.stream.type][mappedSecurityFeatName]) {
		showModal('feature-upgrade');
		setTimeout(() => {
			features.value[featureName].enabled = !features.value[featureName].enabled;
		}, 500);
		return;
	}

	let nstate = feature.enabled;
	feature.enabled = nstate;

	if (feature.valueType === 'bool' || !nstate) {
		if (feature.valueType === 'array' && !nstate) {
			nstate = [];
		}

		const saved = await saveSetting(featureName, nstate);
		if (saved) {
			track(EVENTS.SECURITY_FEATURE_TOGGLED, {
				streamId: streamId.value,
				streamType: props.stream.type,
				featureName,
				enabled: feature.enabled,
			});
		}
	}
}

const toggleDrmProtection = async () => {
	if (featureProcessing.value.drmProtection) return;

	// Plan-gated: if the current plan doesn't include DRM, prompt to upgrade
	// and revert the toggle.
	if (!drmProtectionFlag.value) {
		showModal('feature-upgrade');
		setTimeout(() => {
			features.value.drmProtection.enabled = !features.value.drmProtection.enabled;
		}, 500);
		return;
	}

	const saved = await saveSetting('drmProtection', features.value.drmProtection.enabled);
	if (saved) {
		track(EVENTS.DRM_TOGGLED, {
			streamId: streamId.value,
			streamType: props.stream.type,
			drmProtection: features.value.drmProtection.enabled,
			planHasDrm: drmProtectionFlag.value,
		});
	}
}

const saveGeoBlocking = async () => {
	const updatedSettings = countryBlockSelection.value;
	const saved = await saveSetting('countryBlock', _.map(updatedSettings, 'code'));
	if (saved) {
		track(EVENTS.SECURITY_FEATURE_TOGGLED, {
			streamId: streamId.value,
			streamType: props.stream.type,
			featureName: 'countryBlock',
			enabled: features.value.countryBlock.enabled,
			countryCount: updatedSettings.length,
		});
	}
}

const saveGeoWhitelisting = async () => {
	const updatedSettings = countryWhitelistSelection.value;
	const saved = await saveSetting(
		'countryWhitelists',
		_.map(updatedSettings, 'code')
	);
	if (saved) {
		track(EVENTS.SECURITY_FEATURE_TOGGLED, {
			streamId: streamId.value,
			streamType: props.stream.type,
			featureName: 'countryWhitelists',
			enabled: features.value.countryWhitelists.enabled,
			countryCount: updatedSettings.length,
		});
	}
}

const setStreamPassword = async () => {
	const password = features.value.embedPassword.value;
	if (!password) return;
	const saved = await saveSetting('embedPassword', password);
	if (saved) {
		track(EVENTS.SECURITY_FEATURE_TOGGLED, {
			streamId: streamId.value,
			streamType: props.stream.type,
			featureName: 'embedPassword',
			enabled: features.value.embedPassword.enabled,
		});
	}
}

const saveWhitelistedDomains = async () => {
	let domains = _.split(whitelistedDomains.value, '\n')
		.map(d => d.trim())
		.filter(Boolean);
	const castrExist = domains.find((item) => item === 'https://castr.com');
	if (_.isEmpty(castrExist)) {
		domains = [...domains, 'https://castr.com'];
	}
	if (!domains.length) return;

	const saved = await saveSetting('domainWhitelists', domains);
	if (saved) {
		track(EVENTS.SECURITY_FEATURE_TOGGLED, {
			streamId: streamId.value,
			streamType: props.stream.type,
			featureName: 'domainWhitelists',
			enabled: features.value.domainWhitelists.enabled,
			domainCount: domains.length,
		});
	}
}

const saveWhitelistedPageStatus = async () => {
	await saveSetting('pageWhitelist', features.value.pageWhitelist.enabled, 'Whitelist specific page status updated');
}

const saveSetting = async (key, value, toastMsg = false) => {
	featureProcessing.value[key] = true;
	let success = false;
	try {
		await StreamService.saveStreamMetadata(
			streamId.value,
			key,
			value
		);
		// clear stream meta cache in embed player
		StreamService.clearStreamMetadataCache(streamId.value);
		if (toastMsg) {
			notify({ group: 'info', text: toastMsg });
		}
		success = true;
	} catch (e) {
		notify({ group: 'error', text: 'Unable to save your changes.' });
	}
	featureProcessing.value[key] = false;
	featureDisabled.value[key] = true;

	if (success) {
		const updatedMetadata = { ...(streamMeta.value || props.stream.metadata || {}), [key]: value };
		streamMeta.value = updatedMetadata;
		// Keep StreamCache aligned so later reads don't restore stale metadata.
		store.commit('StreamCache/SET_METADATA', {
			streamId: streamId.value,
			metadata: updatedMetadata,
		}, { root: true });
		// Bubble to StreamsManage so the sidebar embed code reacts without a reload.
		emit('stream-updated', { metadata: updatedMetadata });
	}

	return success;
}

const isDisabled = (option) => {
	if (featureProcessing.value[option]) return true
	if (featureDisabled.value[option]) return true
	return false
}

const enableSave = (feature) => featureDisabled.value[feature] = false;

const getProtectionPageMsgsnippet = () => {
  return `<script>
	window.addEventListener("DOMContentLoaded", () => {
		const iframes = document.querySelectorAll("iframe");
		iframes.forEach((iframe) => {
			const src = iframe.src || "";
			if (src.includes("castr")) {
				iframe.addEventListener("load", () => {
					iframe.contentWindow?.postMessage(
						{
							type: "parent-info",
							parentURL: window.location.href,
						},
						"*"
					);
				});
			}
		});
	});
<\/script>`
}

const clipboardCopy = async (value) => {
  await navigator.clipboard.writeText(value);
  notify({ group: 'info', text: 'Copied to clipboard' });
}


</script>

<template>
	<div class="divide-y divide-surface-3">
		<section class="grid grid-cols-[1fr_auto] gap-x-4 items-center !py-3">
			<div>
				<h4 class="text-base">Anti-Piracy Protection</h4>
				<p class="text-tiny text-surface-7">Protect your videos from unauthorized access, copying, and piracy using DRM-secured playback.</p>
			</div>
			<Tooltip class="justify-self-end" wrapper-class="inline-flex" :active="!drmProtectionFlag">
				<FormSwitch v-model="features.drmProtection.enabled"
					:disabled="!drmProtectionFlag || featureProcessing.drmProtection" @change="toggleDrmProtection" />
				<template #content>
					<a class="underline cursor-pointer" onclick="Intercom('show')">Contact sales</a> to enable this feature.
				</template>
			</Tooltip>
		</section>
		<section class="grid grid-cols-[1fr_auto] gap-x-4 items-center !py-3">
			<div>
				<h4 class="text-base">Enable Password Protection</h4>
				<p class="text-tiny text-surface-7">Viewers must enter this password before they can watch.</p>
			</div>
			<FormSwitch class="justify-self-end" @change="toggleFeature('embedPassword')"
				v-model="features.embedPassword.enabled" :disabled="featureProcessing.embedPassword" />
			<div class="col-span-2 flex items-center gap-x-2 !mt-2" v-if="features.embedPassword.enabled">
				<FormInput class="!w-1/2" v-model="features.embedPassword.value" @input="enableSave('embedPassword')" placeholder="Set a password" />
				<FormButton :disabled="isDisabled('embedPassword')" @click="setStreamPassword">
					{{ featureProcessing.embedPassword ? 'Saving' : 'Save' }}
				</FormButton>
			</div>
		</section>
		<section class="grid grid-cols-[1fr_auto] gap-x-4 items-center !py-3">
			<div>
				<h4 class="text-base">Geo/Country Blocking</h4>
				<p class="text-tiny text-surface-7">Block viewers from specific countries.</p>
			</div>
			<FormSwitch class="justify-self-end" v-model="features.countryBlock.enabled"
				:disabled="featureProcessing.countryBlock" @change="toggleFeature('countryBlock')" />
			<div class="col-span-2 flex items-center gap-x-2 !mt-1" v-if="features.countryBlock.enabled">
				<template v-if="countryListLoaded">
					<multiselect v-model="countryBlockSelection" :options="countryList" :multiple="true" :taggable="true"
						tag-placeholder="Unsupported region" placeholder="Search for a country" label="name" track-by="code"
						class="min-h-[2rem]" @select="enableSave('countryBlock')"  @remove="enableSave('countryBlock')" />
					<FormButton :disabled="isDisabled('countryBlock')" @click="saveGeoBlocking">
						{{ featureProcessing.countryBlock ? 'Saving' : 'Save' }}
					</FormButton>
				</template>
				<template v-else>
					<Spinner />
				</template>
			</div>
		</section>
		<section class="grid grid-cols-[1fr_auto] gap-x-4 items-center !py-3">
			<div>
				<h4 class="text-base">Geo/Country Whitelisting</h4>
				<p class="text-tiny text-surface-7">Allow viewers only from specific countries.</p>
			</div>
			<FormSwitch class="justify-self-end" v-model="features.countryWhitelists.enabled"
				:disabled="featureProcessing.countryWhitelists" @change="toggleFeature('countryWhitelists')" />
			<div class="col-span-2 flex items-center gap-x-2 !mt-1" v-if="features.countryWhitelists.enabled">
				<template v-if="countryListLoaded">
					<multiselect v-model="countryWhitelistSelection" :options="countryList" :multiple="true" :taggable="true"
						tag-placeholder="Unsupported region" placeholder="Search for a country" label="name" track-by="code"
						class="min-h-[2rem]" @select="enableSave('countryWhitelists')" @remove="enableSave('countryWhitelists')" />
					<FormButton :disabled="isDisabled('countryWhitelists')" @click="saveGeoWhitelisting">
						{{ featureProcessing.countryWhitelists ? 'Saving' : 'Save' }}
					</FormButton>
				</template>
				<template v-else>
					<Spinner />
				</template>
			</div>
		</section>
		<section class="grid grid-cols-[1fr_auto] gap-x-4 items-center !py-3">
			<div>
				<h4 class="text-base">Enable Domain Whitelisting</h4>
				<p class="text-tiny text-surface-7">If you want the player to work across your whole website (e.g., example.com), simply add your domain(s) to the whitelist.</p>
			</div>
			<FormSwitch class="justify-self-end" v-model="features.domainWhitelists.enabled"
				:disabled="featureProcessing.domainWhitelists" @change="toggleFeature('domainWhitelists')" />
			<div class="col-span-2 items-center gap-x-2 !mt-1" v-if="features.domainWhitelists.enabled">
				<template v-if="whitelistedDomainsLoaded">
					<div class="flex items-start gap-x-2">
					<FormText v-model="whitelistedDomains" custom-classes="!h-24"
						placeholder="one domain per line" @input="enableSave('domainWhitelists')" />
						<FormButton :disabled="isDisabled('domainWhitelists')"
							@click="saveWhitelistedDomains">
							{{ featureProcessing.domainWhitelists ? 'Saving' : 'Save' }}
						</FormButton>
					</div>
					<p class="text-tiny text-surface-7 mt-2">
						One domain per line, e.g. <code class="text-surface-8">https://example.com</code>
					</p>
				</template>
				<template v-else>
					<Spinner />
				</template>

				<div class="mt-4">
					<FormCheckbox :disabled="!securityWhitelistPage" class=" col-span-2 md:col-span-3 mb-0"  @change="saveWhitelistedPageStatus" v-model="features.pageWhitelist.enabled" label="Whitelist Specific Pages Only" />
					<p class="text-tiny text-surface-7">If you want the player to work on specific pages of your site (e.g., only example.com/page1).</p>
					<p v-if="!securityWhitelistPage" class="text-s-s mb-0 text-surface-7"><a class="text-underline text-reset text-surface-7" target="_blank"
						href="/app/subscribe">Upgrade</a> your plan to access this feature.</p>

					<p v-if="features.pageWhitelist.enabled" class="mt-3">
						Code snippet
					</p>
					<p v-if="features.pageWhitelist.enabled" class="text-tiny text-surface-7">Insert this snippet into the page where the player should be embedded.</p>
				<FormText v-if="features.pageWhitelist.enabled" readonly custom-classes="!h-64 !mb-2 !mt-4"
					@click="clipboardCopy(getProtectionPageMsgsnippet)" :model-value="getProtectionPageMsgsnippet()" />
					<div v-if="features.pageWhitelist.enabled" class="flex gap-x-2 mb-6">
						<FormButton @click="clipboardCopy(getProtectionPageMsgsnippet())" size="sm" type="secondary">
							<icon-base class="mr-2 shrink-0" fill="transparent">
							<icon-copy />
							</icon-base>
							Copy to Clipboard
						</FormButton>
					</div>
				</div>
			</div>

		</section>
	</div>
	<div>
		<confirm-modal modal-id="feature-upgrade" ok-text="Upgrade Now" cancel-text="Later"
			@modal-confirm="navigateToPayments(stream.type)">
			<p class="mb-0 px-6 pb-4 text-s-m"><a target="_blank" class="text-reset text-underline" href="/app/subscribe">Upgrade</a> to
				access this feature. Check <a target="_blank" class="text-reset text-underline text-nowrap"
					href="https://castr.com/pricing">Pricing page</a> to learn more about eligible plans.</p>
		</confirm-modal>
	</div>
</template>

<style>
.multiselect__tags {
	@apply min-h-[2rem] !px-2 !py-0 bg-surface-3 border-none flex flex-wrap gap-1
}

.multiselect__select {
	@apply  min-h-[2rem] h-8 !p-1
}

.multiselect__option {
	@apply min-h-[2rem] !px-2 !py-1 text-tiny flex items-center
}

.multiselect__option::after {
	@apply min-h-[2rem] leading-8 text-surface-7
}

.multiselect__input, .multiselect__single {
	@apply min-h-[2rem] h-8 !mt-0 !px-2 text-tiny text-surface-8 placeholder:text-surface-7
}

.multiselect__input, .multiselect__single {
	@apply min-h-[2rem] text-tiny -mt-1
}

.multiselect__placeholder {
	@apply min-h-[2rem] !px-2 py-0 text-tiny text-surface-8 flex items-center leading-none
}

.multiselect__tag-icon {
	@apply flex items-center justify-center m-0  top-1 right-2
}

.multiselect__tag-icon::after {
	@apply text-tiny leading-none font-[12px] -mt-px
}
.multiselect__placeholder {
	@apply mb-0
}
.multiselect__tags-wrap {
	@apply inline-flex flex-wrap gap-1 pr-8 !py-1
}
.multiselect__tag {
	@apply m-0 text-xs leading-none flex items-center gap-x-2 py-0 h-6 rounded-md
}
</style>